import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, User, Bell, Lock, Building, CreditCard, Shield } from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';

export function EmployerSettings() {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(false);
  const [candidateMessages, setCandidateMessages] = useState(true);
  const [newApplications, setNewApplications] = useState(true);

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/employer/dashboard" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="mb-2 text-gold-gradient">
            Settings
          </h1>
          <p className="text-gray-400">Manage your account and preferences</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="profile" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="company" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Building className="w-4 h-4 mr-2" />
              Company
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Lock className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Profile Information</CardTitle>
                <CardDescription className="text-gray-400">
                  Update your personal details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-6">
                  <Avatar className="w-20 h-20">
                    <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black text-2xl">
                      EC
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 mb-2">
                      Change Photo
                    </Button>
                    <p className="text-xs text-gray-500">JPG or PNG. Max size 2MB.</p>
                  </div>
                </div>

                <Separator className="bg-white/10" />

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">First Name</Label>
                    <Input
                      defaultValue="John"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Last Name</Label>
                    <Input
                      defaultValue="Smith"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Email</Label>
                  <Input
                    type="email"
                    defaultValue="john.smith@company.com"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Phone</Label>
                  <Input
                    type="tel"
                    defaultValue="+44 7700 900000"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Job Title</Label>
                  <Input
                    defaultValue="HR Manager"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <Button className="bg-gold-gradient hover:brightness-110 text-black">
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Tab */}
          <TabsContent value="company">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Company Information</CardTitle>
                <CardDescription className="text-gray-400">
                  Manage your company profile
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-white">Company Name</Label>
                  <Input
                    defaultValue="Stevenage Care Home"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Industry</Label>
                  <Select defaultValue="healthcare">
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="technology">Technology</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Company Size</Label>
                  <Select defaultValue="50-200">
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="50-200">50-200 employees</SelectItem>
                      <SelectItem value="200+">200+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Company Description</Label>
                  <Textarea
                    defaultValue="Leading care provider in Stevenage, dedicated to providing exceptional care services."
                    className="bg-white/10 border-white/20 text-white min-h-32"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Website</Label>
                    <Input
                      type="url"
                      placeholder="https://www.company.com"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">LinkedIn</Label>
                    <Input
                      type="url"
                      placeholder="https://linkedin.com/company/..."
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Address</Label>
                  <Input
                    defaultValue="123 High Street, Stevenage, SG1 1AA"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <Button className="bg-gold-gradient hover:brightness-110 text-black">
                  Update Company Profile
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Notification Preferences</CardTitle>
                <CardDescription className="text-gray-400">
                  Choose how you want to be notified
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white">Email Notifications</p>
                      <p className="text-sm text-gray-400">Receive email updates</p>
                    </div>
                    <Switch
                      checked={emailNotifications}
                      onCheckedChange={setEmailNotifications}
                    />
                  </div>

                  <Separator className="bg-white/10" />

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white">Push Notifications</p>
                      <p className="text-sm text-gray-400">Browser push notifications</p>
                    </div>
                    <Switch
                      checked={pushNotifications}
                      onCheckedChange={setPushNotifications}
                    />
                  </div>

                  <Separator className="bg-white/10" />

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white">New Applications</p>
                      <p className="text-sm text-gray-400">Get notified when candidates apply</p>
                    </div>
                    <Switch
                      checked={newApplications}
                      onCheckedChange={setNewApplications}
                    />
                  </div>

                  <Separator className="bg-white/10" />

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white">Candidate Messages</p>
                      <p className="text-sm text-gray-400">Notify when candidates message you</p>
                    </div>
                    <Switch
                      checked={candidateMessages}
                      onCheckedChange={setCandidateMessages}
                    />
                  </div>

                  <Separator className="bg-white/10" />

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white">Weekly Digest</p>
                      <p className="text-sm text-gray-400">Weekly summary of activity</p>
                    </div>
                    <Switch
                      checked={weeklyDigest}
                      onCheckedChange={setWeeklyDigest}
                    />
                  </div>
                </div>

                <Button className="bg-gold-gradient hover:brightness-110 text-black">
                  Save Preferences
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Security Settings</CardTitle>
                <CardDescription className="text-gray-400">
                  Manage your password and security options
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Current Password</Label>
                    <Input
                      type="password"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">New Password</Label>
                    <Input
                      type="password"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Confirm New Password</Label>
                    <Input
                      type="password"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <Button className="bg-gold-gradient hover:brightness-110 text-black">
                  Update Password
                </Button>

                <Separator className="bg-white/10" />

                <div>
                  <h4 className="text-white mb-4">Two-Factor Authentication</h4>
                  <p className="text-sm text-gray-400 mb-4">
                    Add an extra layer of security to your account
                  </p>
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    Enable 2FA
                  </Button>
                </div>

                <Separator className="bg-white/10" />

                <div>
                  <h4 className="text-white mb-4">Active Sessions</h4>
                  <div className="space-y-3">
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white">Chrome on Windows</p>
                          <p className="text-sm text-gray-400">London, UK • Current session</p>
                        </div>
                        <Shield className="w-5 h-5 text-green-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Billing Information</CardTitle>
                <CardDescription className="text-gray-400">
                  Manage your subscription and payment methods
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-gradient-to-br from-[#edc967]/10 to-transparent border border-[#d2ac47]/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white">Current Plan: Professional</h4>
                    <span className="text-gold-gradient">
                      £99/month
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">Next billing date: December 1, 2025</p>
                  <Link to="/subscription">
                    <Button variant="outline" className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10">
                      Change Plan
                    </Button>
                  </Link>
                </div>

                <Separator className="bg-white/10" />

                <div>
                  <h4 className="text-white mb-4">Payment Method</h4>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-[#ba9036]" />
                        <div>
                          <p className="text-white">•••• •••• •••• 4242</p>
                          <p className="text-sm text-gray-400">Expires 12/2026</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/5">
                        Edit
                      </Button>
                    </div>
                  </div>
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    Add Payment Method
                  </Button>
                </div>

                <Separator className="bg-white/10" />

                <div>
                  <h4 className="text-white mb-4">Billing History</h4>
                  <div className="space-y-2">
                    {[
                      { date: 'Nov 1, 2025', amount: '£99.00', status: 'Paid' },
                      { date: 'Oct 1, 2025', amount: '£99.00', status: 'Paid' },
                      { date: 'Sep 1, 2025', amount: '£99.00', status: 'Paid' },
                    ].map((invoice, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                        <div>
                          <p className="text-white">{invoice.date}</p>
                          <p className="text-sm text-gray-400">{invoice.amount}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-green-400">{invoice.status}</span>
                          <Button size="sm" variant="ghost" className="text-white hover:bg-white/5">
                            Download
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
